import React from 'react';

import { ControllerRenderProps, useFormContext } from 'react-hook-form';

import { z } from 'zod';

import Checkbox from 'components/Checkbox/Checkbox';
import { cn } from 'components/lib/utils';
import UploadImage from 'components/UploadImage/UploadImage';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../../../../components/ui/form';
import { Input } from '../../../../../../components/ui/input';
import { IBlockContentProps } from '../../../constants';
import {
  pageFormSchema,
  progressBarBlockSchema
} from '../../../validation-schema';

type ProgressBarBlockType = z.infer<typeof progressBarBlockSchema>;
type PageFormType = z.infer<typeof pageFormSchema>;

type ProgressBarFieldProps<T extends keyof ProgressBarBlockType> =
  ControllerRenderProps<PageFormType, any> & {
    value: ProgressBarBlockType[T];
  };
export const ProgressBarForm: React.FC<IBlockContentProps> = ({
  block,
  blockIndex
}) => {
  const {
    control,
    formState: { errors }
  } = useFormContext<z.infer<typeof pageFormSchema>>();

  const basePath = `blocksV2.${blockIndex}` as const;

  return (
    <div
      className={
        'flex flex-col gap-4 p-4 rounded-md border transition-colors border-base-border'
      }
    >
      <FormField
        control={control}
        name={`${basePath}.backgroundImage`}
        render={({
          field: { onChange, value }
        }: {
          field: ProgressBarFieldProps<'backgroundImage'>;
        }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Default Background Image"
                description={`Add a background image to your progress bar block.`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className={cn(
                  'w-full',
                  errors[`${basePath}.backgroundImage` as keyof typeof errors]
                    ?.message
                    ? 'mb-0'
                    : ''
                )}
                selectedImageUrl={value ?? undefined}
                data-testid="PagesForm-Upload-BackgroundImage"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.backgroundImageMobile`}
        render={({
          field: { onChange, value }
        }: {
          field: ProgressBarFieldProps<'backgroundImageMobile'>;
        }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Mobile Background Image"
                description={`Add a mobile background image to your progress bar block.`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className={cn(
                  'w-full',
                  errors[
                    `${basePath}.backgroundImageMobile` as keyof typeof errors
                  ]?.message
                    ? 'mb-0'
                    : ''
                )}
                selectedImageUrl={value ?? undefined}
                data-testid="PagesForm-Upload-BackgroundImageMobile"
              />
            </FormControl>
            <FormMessage className="mt-1 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.redirectUrl`}
        render={({
          field
        }: {
          field: ProgressBarFieldProps<'redirectUrl'>;
        }) => (
          <FormItem>
            <FormLabel>Redirect URL</FormLabel>
            <FormControl>
              <Input placeholder="Enter redirect URL" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={`${basePath}.isStoreRedirect`}
        render={({
          field
        }: {
          field: ProgressBarFieldProps<'isStoreRedirect'>;
        }) => (
          <FormItem>
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                label={'External webstore Redirect'}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
